
/* System defined core library */
import { Component, OnInit, Input, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subscription, interval, takeWhile } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

/* User defined library/components */
import { OrchestrationApiService } from '../../shared/services/orchestration-api.service';
import { RoutingService } from '../../../shared/services/routing.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';
import { UtilityService } from '../../../shared/services/utility.service';

/* User defined models */
import { Client } from '../../../shared/models/scheduling/client.model';
import { ContentResponse } from '../../../shared/models/common/shared.model';
import { Program } from '../../../shared/models/scheduling/program.model';

/* User defined enums/constants */
import { ComponentType } from '../../../shared/enums/scheduling.enum';

import { ContentComponent } from '../../../shared/components/content/content.component';

@Component({
  selector: 'app-program-information',
  templateUrl: './program-information.component.html'
})

export class ProgramInformationComponent {

  /* User defined private/public variables  */
  public client: Client;
  public program: Program;
  private stopCondition: Boolean = false;
  public pathLock = false;
  public programs: Program[];
  public resourceCulture: string = "";

  /* Input-OutPut parameters decalartions */
  @Output() public content: ContentResponse;

  @ViewChild(ContentComponent) contentInstance: ContentComponent;

  /* Service subscriptions */
  client$: Subscription;
  program$: Subscription;
  translateLanguage$: Subscription;
  programs$: Subscription;
  resourceCulture$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private router: Router,
    private selectedExamDetailService: SelectedExamDetailService,
    private orchestrationApiService: OrchestrationApiService,
    private utilityService: UtilityService,
    private translate: TranslateService,
    private routingService: RoutingService
  ) {

    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);

    this.client$ = selectedExamDetailService.client.subscribe(
      client => {
        this.client = client;
      });

    this.program$ = selectedExamDetailService.program.subscribe(
      program => {
        this.program = program;
      });

    this.programs$ = selectedExamDetailService.programs.subscribe(
      programs => {
        this.programs = programs;
      });

    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      }
    );

    this.resourceCulture$ = selectedExamDetailService.resourceFileCulture.subscribe(
      resourceCulture => {
        this.resourceCulture = resourceCulture;
        this.getContent();
      });
  }

  /* Initialize the component & call require methods when component loaded first time. */
  ngOnInit() {
    sessionStorage.setItem('previousUrl', window.location.pathname);
    interval(1.5)
      .pipe(takeWhile(() => !this.stopCondition))
      .subscribe(i => {
        this.stopCondition = true;
        if (!this.utilityService.isDataValidToLoadComponent(ComponentType.ProgramInformationComponent)) return;
        else {
          this.getContent();
        }
      });
    this.pathLock = this.programs && this.programs.length > 0 ? this.pathLock : this.selectedExamDetailService.pathLockTestInformation;

    this.orchestrationApiService.getConfigurationAttributes(['Consent'], this.client.code, this.program.code).subscribe(resp => {
      if (!!resp && !!resp[0]) {
        this.selectedExamDetailService.isCustomAgreementRequired = resp[0].ConfigurationValue === 'True';
        this.selectedExamDetailService.customAgreementName = resp[0].Attributes;
      }
    });
  }

  ngAfterViewInit() {
    this.contentInstance.setFocusToContent();
  }

  /* Get the program information from XMl resource file. */
  getContent(): void {
    if (this.program != null && this.program != undefined) {

      this.utilityService.getContent("ProgramInfo", this.resourceCulture, this.program.code).subscribe(content => {
        this.content = content;
      });
    }
  }

  /* Use to move next screen */
  submit(): void {

    if (this.selectedExamDetailService.skipPrivacyPage) {
      this.selectedExamDetailService.setPreapprovedCommit("");
      this.selectedExamDetailService.setPreapprovedSelection("");
    }

    this.routingService.scheduleFwdNavigation(ComponentType.ProgramInformationComponent);

    this.selectedExamDetailService.currentTargetFlow = 'Schedule';
  }

  /* Use to move back to previous screen */
  goToPrevPage(): void {
    this.router.navigate(['/scheduling/scheduleappointment']);
  }
}
